// hooks/useDeliveryScreen.ts
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { DeliveryRule } from "../../common/types/types";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { selectStoreList } from "../../store/slices/storeListSlice";

interface GroupedRules {
  radius: number;
  rules: Array<{
    level: number;
    value: number;
  }>;
}

export const useDeliveryScreen = () => {
  const currentCluster = useSelector(selectCurrentCluster);
  const storeList = useSelector(selectStoreList);

  const hasAnyZones = useMemo(() => {
    return storeList.some((store) => (store.deliveryZones?.length || 0) > 0);
  }, [storeList]);

  const deliveryRulesByRadius = useMemo((): GroupedRules[] => {
    if (!currentCluster?.delivery?.length) return [];

    const rulesMap = new Map<number, Array<{ level: number; value: number }>>();

    currentCluster.delivery.forEach((rule: DeliveryRule) => {
      const radius = rule.radius ? Number(rule.radius) : 0;
      const existing = rulesMap.get(radius) || [];
      rulesMap.set(radius, [
        ...existing,
        {
          level: Number(rule.level),
          value: Number(rule.value),
        },
      ]);
    });

    return Array.from(rulesMap.entries())
      .map(([radius, rules]) => ({
        radius,
        rules: rules.sort((a, b) => a.level - b.level),
      }))
      .sort((a, b) => a.radius - b.radius);
  }, [currentCluster]);

  const bestOffer = useMemo((): DeliveryRule | null => {
    if (!currentCluster?.delivery?.length) return null;
    if (hasAnyZones) return null; // ❌ есть зоны — не показываем
    return currentCluster.delivery.reduce(
      (best: DeliveryRule, current: DeliveryRule) =>
        Number(current.value) < Number(best.value) ? current : best,
    );
  }, [currentCluster, hasAnyZones]);

  const workingHours = useMemo(() => {
    const today = new Date().getDay();
    const todaySchedule = currentCluster?.schedule?.find(
      (s) => s.sequence === today,
    );

    if (todaySchedule?.dayOff) return "Выходной";
    return todaySchedule
      ? `${todaySchedule.from} – ${todaySchedule.to}`
      : "10:00 – 22:00";
  }, [currentCluster]);

  return {
    deliveryRulesByRadius,
    bestOffer,
    workingHours,
    hasDelivery: !!currentCluster?.delivery?.length,
    hasAnyZones,
    phoneNumber: currentCluster?.phone_number || "8 (800) 000-00-00",
  };
};
